import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  AppBar, Toolbar, Typography, Button, Fab, IconButton, Tooltip,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotifIcon from '@mui/icons-material/NotificationImportant';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faUserGraduate, faPowerOff } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';

import axios from '../../services/api';
import { MEIS_EDU_TOKEN } from '../../services/api';
import dataService from '../../services/dataService';
import { logout } from '../../store/authSlice';
import { setClasses } from '../../store/classSlice';
import Spinner from '../Spinner/Spinner';
import AddNotif from './AddNotif';
import LogoImg from '../Main/img/Telekom logo magenta 2022.png';

export default function Admin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user } = useSelector((s) => s.authentication);

  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [razredi, setRazredi] = useState([]);
  const [currentInfo, setCurrentInfo] = useState({ id: null });
  const [mode, setMode] = useState('list');

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = () => {
    setLoading(true);
    setTimeout(async () => {
      try {
        const data = await dataService.loadAllNotifications();
        if (data) {
          setNotifications(data.notifications);
          setRazredi(data.razredi);
        }
      } catch {}
      setLoading(false);
    }, 500);
  };

  const handleLogout = () => {
    localStorage.setItem(MEIS_EDU_TOKEN, null);
    dispatch(logout());
    dispatch(setClasses());
    navigate('/login');
  };

  const onNotifSave = (data) => {
    setLoading(true);
    axios.post('/api/save_notif', data)
      .then((response) => {
        setLoading(false);
        if (response.data.msg === '') {
          loadNotifications();
          setCurrentInfo({ id: null });
          setMode('list');
        }
      })
      .catch(() => setLoading(false));
  };

  const handleDelete = (data) => {
    setLoading(true);
    axios.post('/api/delete_notif', data)
      .then((response) => {
        setLoading(false);
        if (response.data.msg === '') {
          loadNotifications();
          setCurrentInfo({ id: null });
          setMode('list');
        }
      })
      .catch(() => setLoading(false));
  };

  const handleNotif = (data) => {
    setLoading(true);
    setTimeout(() => {
      axios.post('/api/send_notif', data)
        .then(() => setLoading(false))
        .catch(() => setLoading(false));
    }, 1000);
  };

  const getDatum = (ts) => {
    const datum = new Date(ts);
    return dayjs(datum).format('DD.MM.YYYY HH:mm');
  };

  return (
    <div>
      <div>
        <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar>
            {!isMobile && (
              <Typography variant="h6" sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-start' }}>
                Administracija Dnevnika
              </Typography>
            )}
            {!isMobile && (
              <>
                <div style={{ marginRight: 10 }}>{user}</div>
                <div style={{ marginRight: 20 }}>
                  <FontAwesomeIcon icon={faUserGraduate} size="2x" />
                </div>
                <Tooltip title="Odjavi se" arrow>
                  <Button color="inherit" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faPowerOff} size="2x" />
                  </Button>
                </Tooltip>
              </>
            )}
          </Toolbar>
        </AppBar>

        {loading && <Spinner />}

        <main style={{ minHeight: 'calc(100vh - 132px)', flexGrow: 1, padding: isMobile ? 0 : 16 }}>
          <Toolbar />

          {mode === 'list' && (
            <div className="admin-notif-header">
              <div><Typography variant="h5">Pregled obavještenja</Typography></div>
              <div>
                <Fab size="small" color="primary" onClick={() => { setCurrentInfo({ id: null }); setMode('edit'); }}>
                  <AddIcon />
                </Fab>
              </div>
            </div>
          )}

          {mode === 'edit' && (
            <div className="admin-notif-header">
              <div><Typography variant="h5">Dodaj obavještenje</Typography></div>
              <div>
                <Fab size="small" color="primary" onClick={() => { setCurrentInfo({ id: null }); setMode('list'); }}>
                  <FontAwesomeIcon icon={faArrowLeft} size="1x" />
                </Fab>
              </div>
            </div>
          )}

          {mode === 'list' && (
            <div>
              {notifications.map((item, index) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography component="h2">{getDatum(item.ts) + ' - ' + item.headline}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className="right-flex-container">
                      <div className="admin-toolbal-header">
                        <Fab size="small" color="secondary" sx={{ ml: 0.5 }}
                          onClick={() => handleDelete(item)}>
                          <DeleteIcon />
                        </Fab>
                        <Fab size="small" color="primary" sx={{ ml: 0.5 }}
                          onClick={() => { setCurrentInfo(item); setMode('edit'); }}>
                          <EditIcon />
                        </Fab>
                        <Fab size="small" color="primary" sx={{ ml: 0.5 }}
                          onClick={() => handleNotif(item)}>
                          <NotifIcon />
                        </Fab>
                      </div>
                      <div className="bottom-flex-container w-100" style={{ marginTop: 5 }}>
                        <div className="w-100" dangerouslySetInnerHTML={{ __html: item.body }} />
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
          )}

          {mode === 'edit' && (
            <AddNotif onSave={onNotifSave} data={currentInfo} razredi={razredi} />
          )}
        </main>
        <div style={{ height: 50 }} />
      </div>

      <div className="admin-footer centered-container" style={{ zIndex: 999999999 }}>
        <div style={{ marginLeft: 10 }}>Copyright &copy; Ministarstvo prosvjete, nauke i inovacija Crne Gore 2026</div>
        <div className="centered-container-row">
          <div style={{ marginRight: 10 }}>U saradnji sa </div>
          <a href="http://www.telekom.me" style={{ margin: 3 }} className="centered-container-row">
            <img src={LogoImg} style={{ marginRight: 10, height: 35 }} alt="Telekom" />
          </a>
        </div>
      </div>
    </div>
  );
}
